import { getQuestions } from "../../api.services";
import { useState, useEffect, useRef, useCallback } from "react";
import "../exam/exam.css";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { examSubmit } from "../../results.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faArrowLeft, faBell } from "@fortawesome/free-solid-svg-icons";
import SubmitConfirmationModal from "../modals/submit-confirmation";
import ExitModal from "../modals/exit-modal";
import { SpinnerDotted } from "spinners-react";

/* ------------------------------------------------------------------ */
/*  CONSTANTS                                                         */
/* ------------------------------------------------------------------ */
const MINUTE_SECONDS = 60;
const EXAM_DURATION = 600; // 10 minutes
const SCORE_CHECKPOINT = 80;
const TOTAL_QUESTIONS = 20;
const POINTS_PER_QUESTION = 5;

const TIMER_PROPS = {
  size: 120,
  strokeWidth: 6,
};

/* ------------------------------------------------------------------ */
/*  SMALL HELPER COMPONENTS                                           */
/* ------------------------------------------------------------------ */
const BackButtonListener = () => {
  useEffect(() => {
    const handler = () => {
      window.location.href = "/evaluacion/home";
    };
    window.onpopstate = handler;
    return () => {
      window.onpopstate = null;
    };
  }, []);
  return null;
};

const WinnerAlert = ({ email }) => {
  const [showAlert, setShowAlert] = useState(true);
  if (!showAlert) return null;
  return (
    <div className="flex text-white px-6 py-4 border-0 rounded relative mb-4 bg-blue-500">
      <span className="text-xl inline-block mr-5 align-middle">
        <FontAwesomeIcon icon={faBell} />
      </span>
      <span className="inline-block align-middle mr-8">
        <b className="capitalize"> ¡Felicidades! </b> Por obtener calificaciones
        sobresalientes acabas de obtener un certificado al correo:{" "}
        <b className="text-blue-900">{email}</b>
      </span>
      <button
        className="absolute bg-transparent text-2xl font-semibold leading-none right-0 top-0 mt-4 mr-6 outline-none focus:outline-none w-min border-none hover:bg-transparent"
        onClick={() => setShowAlert(false)}
      >
        <span>×</span>
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  SCORE CARD (used in results)                                      */
/* ------------------------------------------------------------------ */
const ScoreCard = ({ value, label, textColor }) => (
  <div className="disabled-link flex flex-col justify-between p-8 transition-shadow bg-white rounded-3xl shadow-xl group hover:shadow-lg w-60 h-50">
    <div>
      <h5 className={`text-5xl font-bold ${textColor}`}>
        {value}
        {label === "Calificación" && <p className="text-[22px]">/100</p>}
      </h5>
      <div className="pt-2 mt-4 border-t-2 border-indigo-100">
        <p className="text-sm font-medium tracking-widest text-gray-500 uppercase">
          {label}
        </p>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  RESULTS VIEW COMPONENT                                            */
/* ------------------------------------------------------------------ */
const ExamResults = ({
  questions,
  tempAnswers,
  state,
  onBackHome,
  onExit,
  score,
}) => {
  const correctCount = score / POINTS_PER_QUESTION;
  const incorrectCount = TOTAL_QUESTIONS - correctCount;

  return (
    <div>
      {/* Winner alert */}
      {score >= SCORE_CHECKPOINT && (
        <div className="spacing-height" key="winner-alert">
          <WinnerAlert email={state.email} />
        </div>
      )}

      {/* Back button */}
      <div className="grid col-start-1" key="back-btn">
        <div
          className="bg-black rounded-xl m-4 p-4 justify-left w-min flex cursor-pointer hover:bg-gray-700"
          onClick={onBackHome}
        >
          <FontAwesomeIcon icon={faHome} />
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
        </div>
      </div>

      {/* Scoreboard */}
      <div className="spacing-height lg:mr-20" key="scoreboard">
        <span className="text-2xl lg:ml-20">
          Resultados de{" "}
          <em>
            {state.name} {state.lastName}
          </em>
        </span>

        <div className="spacing-height results-card lg:pl-20">
          <ScoreCard
            value={score}
            label="Calificación"
            textColor="text-indigo-600"
          />
          <br />
          <div className="lg:mr-10 lg:ml-10">
            <ScoreCard
              value={correctCount}
              label="Correctas"
              textColor="text-[#008000]"
            />
          </div>
          <br />
          <ScoreCard
            value={incorrectCount}
            label="Incorrectas"
            textColor="text-red-700"
          />
        </div>
        <div className="spacing-height"></div>
      </div>

      {/* Results per Question */}
      {questions.map((pregunta, idx) => (
        <main className="app" key={`result-q-${pregunta.id || idx}`}>
          <div className="lado-derecho">
            <div className="numero-pregunta">
              <span>
                Pregunta <b>{pregunta.id}</b> de
              </span>{" "}
              <b>{TOTAL_QUESTIONS}</b>
            </div>
            <div className="pregunta-box">
              <div className="titulo-pregunta">{pregunta.titulo}</div>
            </div>

            <div className="answer-text lg:grid lg:grid-cols-2 gap-4">
              {pregunta?.opciones?.map((respuesta, rIdx) => (
                <div
                  className={respuesta.classString}
                  key={`result-a-${pregunta.id}-${rIdx}`}
                >
                  <button disabled className="lg:mt-0 mt-5">
                    {respuesta.textoRespuesta}
                  </button>

                  {/* Check icon */}
                  <svg
                    className="check hidden-check"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z" />
                  </svg>

                  {/* X icon */}
                  <svg
                    className="xmark hidden-xmark"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM175 208.1L222.1 255.1L175 303C165.7 312.4 165.7 327.6 175 336.1C184.4 346.3 199.6 346.3 208.1 336.1L255.1 289.9L303 336.1C312.4 346.3 327.6 346.3 336.1 336.1C346.3 327.6 346.3 312.4 336.1 303L289.9 255.1L336.1 208.1C346.3 199.6 346.3 184.4 336.1 175C327.6 165.7 312.4 165.7 303 175L255.1 222.1L208.1 175C199.6 165.7 184.4 165.7 175 175C165.7 184.4 165.7 199.6 175 208.1V208.1z" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
          <BackButtonListener />
        </main>
      ))}

      {/* Exit button */}
      <div className="timer" key="exit-btn">
        <div className="finish-buttons-container">
          <button className="exit-button" onClick={onExit}>
            Salir
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/*  MAIN EXAM COMPONENT                                               */
/* ================================================================== */
function Exam() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // ---- Redirect if no registration data ----
  useEffect(() => {
    if (!state?.name) {
      navigate("/evaluacion/home");
    }
  }, [state, navigate]);

  // ---- Refs ----
  const scrollListener = useRef(null);
  const timerEventRef = useRef(null);
  const eventosRef = useRef([]);

  // ---- State ----
  const [puntuacion, setPuntuacion] = useState(0);
  const [isFetched, setIsFetched] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [tiempoRestante, setTiempoRestante] = useState(EXAM_DURATION);
  const [areDisabled, setAreDisabled] = useState(false);
  const [answersShown, setAnswersShown] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [tempAnswers, setTempAnswers] = useState([]);
  const [resultsPosted, setResultsPosted] = useState(false);
  const [participant, setParticipant] = useState({});
  const [examResult, setExamResult] = useState({});
  const [isTimerPlaying, setIsTimerPlaying] = useState(true);

  const [timerHidden, setTimerHidden] = useState(true);
  const [clickedStyle, setClickedStyle] = useState("show-time");
  const [invalidExam, setInvalidExam] = useState(false);
  const [postCheck, setPostCheck] = useState(false);

  const [openSendModal, setOpenSendModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [reachedBottom, setReachedBottom] = useState(false);

  // ---- Clear all state ----
  const clearAll = useCallback(() => {
    setPuntuacion(0);
    setIsFetched(false);
    setTempAnswers([]);
    setQuestions([]);
  }, []);

  // ---- Fetch questions on mount ----
  useEffect(() => {
    if (isFetched) return;

    localStorage.clear();
    setLoadingQuestions(true);

    getQuestions()
      .then((questionList) => {
        // Validate questions
        const isInvalid = questionList.some(
          (q) =>
            !q ||
            !q.descripcion === undefined ||
            !q.opciones ||
            q.opciones.length === 0,
        );
        if (isInvalid) {
          setInvalidExam(true);
          return;
        }
        setQuestions(questionList);
        setIsFetched(true);
      })
      .catch((err) => {
        console.error("Error loading questions:", err);
        setInvalidExam(true);
      })
      .finally(() => {
        setLoadingQuestions(false);
      });
  }, [isFetched]);

  // ---- Redirect on page refresh ----
  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation");
    if (navEntries.length > 0 && navEntries[0].type === "reload") {
      window.location.href = "/evaluacion/home";
    }
  }, []);

  // ---- Clean up on page unload ----
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.clear();
      clearAll();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [clearAll]);

  // ---- Timer countdown ----
  useEffect(() => {
    if (tiempoRestante <= 0) return;

    const intervalo = setInterval(() => {
      setTiempoRestante((prev) => {
        if (prev <= 1) {
          setAreDisabled(true);
          confirmSubmition();
          setIsTimerPlaying(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalo);
  }, [tiempoRestante]);

  // ---- POST exam results ----
  useEffect(() => {
    if (!resultsPosted) return;

    examSubmit(participant, examResult).then(() => {
      setResultsPosted(false);
      window.scrollTo(0, 0);
      setParticipant({});
    });
  }, [postCheck]);

  // ---- Scroll handler ----
  const onScroll = useCallback(
    (e) => {
      if (scrollListener.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          scrollListener.current;
        const threshold = scrollHeight * 0.85;
        if (scrollTop + clientHeight >= threshold) {
          if (!reachedBottom) {
            timerEventRef.current = e;
            setReachedBottom(true);
          }
        } else {
          if (reachedBottom) {
            setReachedBottom(false);
          }
        }
      }
    },
    [reachedBottom],
  );

  // ---- Store participant answers ----
  const storeAnswers = useCallback((index, answer, e, answerNumber) => {
    // Update selection styling
    const prevEvent = eventosRef.current[index - 1];
    if (prevEvent) {
      prevEvent.target.classList.remove("correct");
    }
    e.target.classList.add("correct");

    const questionString = "pregunta" + index;
    const storeObj = {
      id: questionString,
      result: answer,
      index: answerNumber,
    };

    localStorage.setItem(questionString, answer);

    // Update tempAnswers immutably
    setTempAnswers((prev) => {
      const updated = [...prev];
      const existingIdx = updated.findIndex((t) => t.id === questionString);
      if (existingIdx >= 0) {
        updated[existingIdx] = {
          ...updated[existingIdx],
          result: answer,
          index: answerNumber,
        };
      } else {
        updated.push(storeObj);
      }
      return updated;
    });

    // Store event ref
    eventosRef.current[index - 1] = e;
  }, []);

  // ---- Timer toggle (mobile) ----
  const updateTimer = useCallback(() => {
    setTimerHidden((prev) => {
      setClickedStyle(prev ? "hidden-timer" : "show-time");
      return !prev;
    });
  }, []);

  // ---- Navigation ----
  const backHome = useCallback(
    (e) => {
      if (e) e.preventDefault();
      window.location.href = "/evaluacion/home";
      navigate("/evaluacion/home", { replace: true });
    },
    [navigate],
  );

  const confirmSubmition = useCallback(() => {
    setTiempoRestante(0);
    setPostCheck((prev) => !prev);
    setAnswersShown(true);
    setIsTimerPlaying(false);
  }, []);

  const exitExam = useCallback(() => {
    window.location.href = "/evaluacion/home";
    navigate("/evaluacion/home");
    setIsFetched(false);
    clearAll();
  }, [navigate, clearAll]);

  /* ================================================================ */
  /*  RESULTS VIEW                                                    */
  /* ================================================================ */
  if (answersShown || tiempoRestante === 0) {
    let score = 0;
    let ansArray = [];

    // Process each question for results
    const processedQuestions = questions.map((pregunta, qIdx) => {
      const questionNumber = qIdx + 1;
      const questionKey = "pregunta" + questionNumber;
      const storedValue = localStorage.getItem(questionKey);

      // Deep copy options to avoid mutating state
      const processedOptions = pregunta.opciones.map((opcion, optIdx) => {
        const opt = { ...opcion };

        if (storedValue === "true") {
          // Correct answer selected
          const tempAnsObj = {
            questionId: opt.questionId,
            value: opt.isCorrect,
            answerId: opt?.answerId,
          };
          ansArray.push(tempAnsObj);

          if (opt.isCorrect) {
            opt.classString = "show-check correct-bg-answer";
          } else {
            opt.classString = "disabled";
          }
        } else if (storedValue === "false") {
          // Wrong answer selected
          if (opt.isCorrect) {
            opt.classString = "green-bg-answer";
          } else {
            const matchingAnswer = tempAnswers.find(
              (ans) => ans.id === questionKey,
            );
            if (matchingAnswer) {
              const tempAnsObj = {
                questionId: opt.questionId,
                value: opt.isCorrect,
              };

              if (
                matchingAnswer.index === undefined ||
                optIdx === matchingAnswer.index
              ) {
                tempAnsObj.answerId = opt.answerId;
                opt.classString = "show-x";
              }
              ansArray.push(tempAnsObj);
            }
            if (!opt.classString) {
              opt.classString = "disabled";
            }
          }
        } else {
          // Unanswered
          opt.classString = "disabled";
        }

        return opt;
      });

      if (storedValue === "true") {
        score += POINTS_PER_QUESTION;
      }

      return { ...pregunta, opciones: processedOptions };
    });

    // Letter grade
    let letter;
    if (score >= 96) letter = "A+";
    else if (score >= 90) letter = "A";
    else if (score >= 85) letter = "B+";
    else if (score >= 80) letter = "B";

    // POST results once
    if (!resultsPosted) {
      setParticipant(state);
      setExamResult({
        grade: score,
        correctCount: score / POINTS_PER_QUESTION,
        incorrectCount: (100 - score) / POINTS_PER_QUESTION,
        letter: letter,
        answers: ansArray,
      });
      setResultsPosted(true);
    }

    return (
      <ExamResults
        questions={processedQuestions}
        tempAnswers={tempAnswers}
        state={state}
        onBackHome={backHome}
        onExit={() => {
          navigate("/evaluacion/home");
          window.location.href = "/evaluacion/home";
          setIsFetched(false);
          clearAll();
        }}
        score={score}
      />
    );
  }

  /* ================================================================ */
  /*  EXAM VIEW                                                       */
  /* ================================================================ */

  // Timer display values
  const minutes = Math.floor(tiempoRestante / 60);
  let seconds = tiempoRestante - minutes * 60;
  const displaySeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

  return (
    <div>
      {/* Modals */}
      {openSendModal && (
        <SubmitConfirmationModal
          key="send-modal"
          openSendModal={setOpenSendModal}
          submitExam={() => {}}
          send={confirmSubmition}
        />
      )}
      {openCancelModal && (
        <ExitModal
          key="cancel-modal"
          openExitModal={setOpenCancelModal}
          exit={exitExam}
        />
      )}

      {/* Loading spinner */}
      <SpinnerDotted
        className="m-10"
        size={50}
        enabled={!isFetched}
        color={"#643A90"}
        key="spinner"
      />

      {/* Timer */}
      {isFetched && (
        <div
          className={`timers-circles ${clickedStyle} sticky-style`}
          key="timer-container"
        >
          <span className="font-bold text-lg">Tiempo Restante</span>
          <span className="xl:hidden mr-[0.25rem]">=</span>

          <div
            className={clickedStyle}
            onScroll={onScroll}
            ref={scrollListener}
          >
            <div className="flex xl:mt-5">
              <CountdownCircleTimer
                {...TIMER_PROPS}
                isPlaying={isTimerPlaying}
                colors={["#061a40", "#B900E2", "#003559", "#003559"]}
                colorsTime={[6, 3, 1, 0]}
                duration={EXAM_DURATION}
                initialRemainingTime={tiempoRestante}
              >
                {({ color }) => (
                  <span style={{ color }}>
                    <div className="time">{minutes}</div>
                    minutos
                  </span>
                )}
              </CountdownCircleTimer>
              <div className="spacing"></div>
              <CountdownCircleTimer
                {...TIMER_PROPS}
                isPlaying={isTimerPlaying}
                colors={["#061a40", "#B900E2", "#003559", "#003559"]}
                colorsTime={[45, 30, 15, 0]}
                duration={MINUTE_SECONDS}
                initialRemainingTime={MINUTE_SECONDS}
                onComplete={() => ({
                  shouldRepeat: minutes > 0,
                  delay: 0.8,
                })}
              >
                {({ color }) => (
                  <span style={{ color }}>
                    <div className="time">{displaySeconds}</div>
                    segundos
                  </span>
                )}
              </CountdownCircleTimer>
            </div>
          </div>
        </div>
      )}

      {/* Mobile timer toggle */}
      <button
        key="timer-toggle"
        className="2xl:hidden lg:mt-[-6rem] lg:mr-[2rem] rounded-full bg-[#4A0094] hover:bg-[#4A0094] w-[55px] h-[55px] timer-minimizer"
        onClick={updateTimer}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-16 w-16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      <div className="mt-[10.5rem]" key="spacer"></div>

      {/* Question Boxes */}
      {questions.map((pregunta, qIdx) => (
        <main className="app t-3" key={`exam-q-${pregunta.id || qIdx}`}>
          <div className="lado-derecho">
            <div className="numero-pregunta">
              <span>
                Pregunta <b>{pregunta.id}</b> de
              </span>{" "}
              <b>{TOTAL_QUESTIONS}</b>
            </div>
            <div className="pregunta-box">
              <div
                className="titulo-pregunta"
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
              >
                {pregunta.titulo}
              </div>
            </div>

            <div className="answer-text lg:grid lg:grid-cols-2 lg:gap-4">
              {pregunta?.opciones?.map((respuesta, rIdx) => (
                <div className="box" key={`ans-${pregunta.id}-${rIdx}`}>
                  <button
                    className="mobile-exam"
                    disabled={areDisabled}
                    onClick={(e) =>
                      storeAnswers(
                        pregunta.id,
                        respuesta.isCorrect,
                        e,
                        respuesta.questionIndex,
                      )
                    }
                    onCopy={(e) => {
                      e.preventDefault();
                      return false;
                    }}
                  >
                    {respuesta.textoRespuesta}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <BackButtonListener />
        </main>
      ))}

      {/* Submit / Cancel buttons */}
      <div className="timer" key="action-buttons">
        <div className="finish-buttons-container">
          <button className="finish" onClick={() => setOpenSendModal(true)}>
            Entregar
          </button>
          <button
            className="finish-cancel"
            onClick={() => setOpenCancelModal(true)}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default Exam;
